import { forwardRef, useCallback, useImperativeHandle, useState } from "react";
import { Alert, Button, Descriptions, Modal, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { DialogRef } from "@/types/common";
import {
  getScanOrderShipmentReport,
  sendScanOrderShipmentReport,
} from "@/apis/tms/order";
import type { InstaShippedPackageItem, InstaShipmentReport } from "@/types/tms";
import Indicator from "@/components/shared/Indicator";

const itemColumns: ColumnsType<InstaShippedPackageItem> = [
  { title: "Product No", dataIndex: "productNo", key: "productNo" },
  { title: "Product Name", dataIndex: "productName", key: "productName" },
  {
    title: "Serial No",
    dataIndex: "serialNo",
    key: "serialNo",
    render: (v) => v ?? <span className="text-gray-400">—</span>,
  },
];

interface ReportDialogProps {
  className?: string;
  onSuccess?: () => void;
}

export const ReportDialog = forwardRef<DialogRef, ReportDialogProps>(
  (props, ref) => {
    const { className, onSuccess } = props;
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [riiOrderNo, setRiiOrderNo] = useState<string | undefined>(undefined);
    const [report, setReport] = useState<InstaShipmentReport | undefined>(
      undefined,
    );
    const [error, setError] = useState<string | undefined>(undefined);

    const closeDialog = useCallback(() => {
      setOpen(false);
      setReport(undefined);
      setError(undefined);
      setLoading(false);
      setRiiOrderNo(undefined);
    }, []);

    const handleSendReport = useCallback(() => {
      if (!riiOrderNo) return;
      setLoading(true);
      sendScanOrderShipmentReport(riiOrderNo)
        .then(() => {
          closeDialog();
          onSuccess?.();
        })
        .catch((e) => setError(`Failed to send shipment report: ${e.msg}`))
        .finally(() => setLoading(false));
    }, [riiOrderNo, closeDialog, onSuccess]);

    useImperativeHandle(ref, () => ({
      showModal: (props?: unknown) => {
        const riiOrderNo = (props as Record<string, unknown> | undefined)
          ?.riiOrderNo as string | undefined;
        if (!riiOrderNo) return;
        setOpen(true);
        setLoading(true);
        setReport(undefined);
        setError(undefined);
        setRiiOrderNo(riiOrderNo);
        getScanOrderShipmentReport(riiOrderNo)
          .then(setReport)
          .catch((e) => setError(`Failed to fetch shipment report: ${e.msg}`))
          .finally(() => setLoading(false));
      },
      hideModal: closeDialog,
    }));

    return (
      <Modal
        className={className}
        title="Shipment Report"
        open={open}
        onCancel={closeDialog}
        footer={null}
        width={800}
        loading={loading}
        maskClosable={false}
      >
        {error && !loading && (
          <Alert className="!mb-2" type="error" message={error} showIcon />
        )}

        {report && !loading && (
          <div>
            <Descriptions bordered size="small" column={2}>
              <Descriptions.Item label="Customer Order No">
                {report.customerOrderNo}
              </Descriptions.Item>
              <Descriptions.Item label="RII Order No">
                {report.riiOrderNo}
              </Descriptions.Item>
              <Descriptions.Item label="Platform">
                <Tag>{report.platform}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Shipping Method">
                {report.shippingMethod}
              </Descriptions.Item>
              <Descriptions.Item label="Shipped Date" span={2}>
                {new Date(report.shippedDate).toLocaleString()}
              </Descriptions.Item>
            </Descriptions>

            <Indicator
              label="Packages"
              hint="scroll vertically to view all packages"
            />
            <div className="max-h-80 overflow-y-auto global__no-scrollbar">
              {report.packages.map((pkg, idx) => (
                <div key={pkg.trackingNo} className="mb-2">
                  <div className="text-sm font-medium mb-1 ml-6">{`# ${idx + 1}`}</div>
                  <Descriptions bordered size="small" column={2}>
                    <Descriptions.Item label="Tracking No">
                      {pkg.trackingNo}
                    </Descriptions.Item>
                    <Descriptions.Item label="SSCC">
                      {pkg.sscc}
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Weight">
                      {pkg.totalWeight} {pkg.weightUnit}
                    </Descriptions.Item>
                    <Descriptions.Item label="Total Volume">
                      {pkg.totalVolume} {pkg.volumeUnit}
                    </Descriptions.Item>
                  </Descriptions>
                  <Table
                    className="mt-2 border-gray-200"
                    size="small"
                    bordered
                    dataSource={pkg.items}
                    columns={itemColumns}
                    rowKey={(_, index) => `${index}`}
                    pagination={false}
                  />
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="flex flex-row justify-end gap-2 mt-4">
          <Button onClick={closeDialog}>Close</Button>
          <Button
            type="primary"
            loading={loading}
            disabled={!riiOrderNo || report == null}
            onClick={handleSendReport}
          >
            Report Shipments
          </Button>
        </div>
      </Modal>
    );
  },
);
